<script lang="ts" setup>
    import '../../../../css/choice.css';
    import {computed,onMounted,watch,reactive} from 'vue';   
    import {IClaim,ClaimDisplay,ClaimSimpleLists,transferTypeWarning,claimLegacyWindowOpen} from '../../../lib/Models/ClaimModel'; 
    import ClaimList from '../Subcomponents/ClaimList.vue';
    import TextInput from '../../../components/TextInput.vue';
    import ActionButtonV2 from '../../../components/ActionButtonV2.vue'; 
    import SimpleList from '../../../components/SimpleList.vue';
    import {ActionButtonStyle} from '../../../lib/Types/Types'; 
    import {ButtonDisplaySize,ButtonDisplayStyle} from '../../../Enums/UiEnums';
    import {prettyCurrency} from '../../../lib/Formatting/Number';   
    import {initPinia} from '../../../lib/PiniaMgr';
    import {useRefundFullfillmentStore} from '../../../lib/Stores/RefundFullfillmentStore'; 
 
    const emit = defineEmits(["saveTransfers"]);
    const refundFullfillmentStore = useRefundFullfillmentStore(initPinia()); 
    
    const formData = reactive <
    {   
        claimRefundListing?: IClaim[],
        claimTransferListing?: IClaim[],
        paymentTypeID: string,
        paymentMethodID: string, 
        disableTransfers: boolean,
        notes?: string,
        window: {headerHeight: number, footerHeight: number, windowPadding: number, workareaHeight: number},
    }>({      
        paymentTypeID: "",
        paymentMethodID: "", 
        disableTransfers: true,
        notes: "",
        window: {headerHeight: 7.5, footerHeight: 6.5, windowPadding: 2.5, workareaHeight: 7}, 
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        claimRefundList?: IClaim[],
        claimTransferList?: IClaim[],
        autodistribute?: boolean
    }>(),
    {
        saveEnabled: 0,
        autodistribute: false
    }); 
    
    onMounted(async () => {  
        resetHeights();
        await refundFullfillmentStore.getTransferSettings();
        formData.paymentTypeID = refundFullfillmentStore.getTransferType();
        formData.paymentMethodID = refundFullfillmentStore.getTransferMethod();
         
        if (!props.claimRefundList) {
            return;
        }
        if (!props.claimTransferList) {
            return;
        }
        formData.claimRefundListing = props.claimRefundList;
        formData.claimTransferListing = props.claimTransferList;
    });
    watch(() => props.claimRefundList, (newclaimList : IClaim[] | undefined, oldclaimList : IClaim[] | undefined) => { 
        if (!newclaimList) {  
            refundFullfillmentStore.clearRefunds();
            return;
        }
        if (JSON.stringify(newclaimList) != JSON.stringify(oldclaimList) ) {  
            refundFullfillmentStore.clearRefunds();
            formData.claimRefundListing = newclaimList; 
        }
    });
    watch(() => props.claimTransferList, (newclaimList : IClaim[] | undefined, oldclaimList : IClaim[] | undefined) => { 
        if (!newclaimList) {
            refundFullfillmentStore.clearProcedureTransfers(0,props.autodistribute);
            return;
        }
        if (JSON.stringify(newclaimList) != JSON.stringify(oldclaimList) ) { 
            refundFullfillmentStore.clearProcedureTransfers(newclaimList.length,props.autodistribute);
            formData.claimTransferListing = newclaimList; 
        }
    });
    const showTransfers = computed(()=> {
        if ((formData.claimRefundListing?.length ?? 0) === 0) {
            return false; 
        }
        if ((formData.claimTransferListing?.length ?? 0) === 0) {
            return false; 
        }
        return true; 
    });
    const allocatedClass = computed(()=> {
        if (!refundFullfillmentStore.transferTotals.AllocateError) {
            return "billing-cell text-end"; 
        } 
        return "billing-cell text-end text-danger"; 
    });
    const updatedBalanceClass = computed(()=> {
        if (!refundFullfillmentStore.transferTotals.TransferError) {
            return "billing-cell text-end"; 
        }
        return "billing-cell text-end text-danger"; 
    });
    const updatedCreditClass = computed(()=> {
        if (!refundFullfillmentStore.refund_error) {
            return "billing-cell text-end"; 
        } 
        return "billing-cell text-end text-danger"; 
    });
    async function saveTransfers() : Promise<void> {
        const refunds_transferred = await refundFullfillmentStore.saveTransfers(formData.paymentMethodID, formData.paymentTypeID, formData.notes);
        if (refunds_transferred) {
            emit("saveTransfers");
        }
    }
    function resetHeights(): void { 
        const windowHeight = (window.innerHeight / 16) * .85;
        formData.window.workareaHeight = windowHeight - formData.window.headerHeight - formData.window.footerHeight - formData.window.windowPadding;
    }
    function openPatient() : void  { 
        if (!refundFullfillmentStore.refundPatientCreditBalance.patientID) {  
            return;
        } 
        const patientURL = `/officemd/screens/WindowSetup.htm?WT=Patient Setup&Qualifier=patient&WindowID=${refundFullfillmentStore.refundPatientCreditBalance.patientID}&tab=Demographics`;
        claimLegacyWindowOpen("NewPatient", patientURL); 
    } 
    function onChangePaymentType(selectedItem : any) : void { 
        formData.paymentTypeID = selectedItem.value;
    } 
    function onChangePaymentMethod(selectedItem : any) : void { 
        formData.paymentMethodID = selectedItem.value;
    }
</script> 
 
<style scoped> 
.billing-header {
  color: #204D89;
  background-color: #f1f1f1;
}
.billing-data-left {
  color: black;
  background-color: white;
  text-align: left;
}    
.billing-data-center {
  color: black;
  background-color: white;
  text-align: center;
}
.billing-data-right {
  color: black;
  background-color: white;
  text-align: right;
}
.billing-cell {
  border: 1px solid #6A8CCB;
  FONT-SIZE: 8pt;
  font-family: Arial, sans-serif, Helvetica;
  padding: 5px 5px 5px 5px;
  cursor: default;
  vertical-align: middle;
}
</style>
<template>
    <div class="row skinnyscroll" :style="{ 'overflow-y': 'scroll', 'overflow-x': 'hidden',  'height': (formData.window.headerHeight + 'rem')}">
        <div class="col" >
            <table border="1" aria-hidden="true" style="table-layout: fixed; border-collapse: collapse; width: 47.5rem;">
                <colgroup>
                    <col style="width: 6.5rem">
                    <col style="width: 5.5rem">
                    <col style="width: 6.6rem">
                    <col style="width: 5.5rem">
                    <col style="width: 6.5rem">
                    <col style="width: 5.5rem">
                    <col style="width: 6.5rem">
                    <col style="width: 5rem">
                </colgroup>
                <tr>
                    <td class="billing-header billing-cell">Patient</td>
                    <td colspan=3 class="billing-cell">
                        
                        <span class="isalus-link pe-2" @click="openPatient">
                                {{refundFullfillmentStore.refundPatientCreditBalance.patientID}} 
                        </span>
                        <span :title="refundFullfillmentStore.refundPatientCreditBalance.patientTooltip">
                            {{refundFullfillmentStore.refundPatientCreditBalance.patientName}}
                        </span>
                    </td>
                    <td class="billing-header billing-cell">Current Balance</td>
                    <td class="billing-cell text-end">{{prettyCurrency(refundFullfillmentStore.refundPatientCreditBalance.patientBalance ?? 0,{negativeCurrencyDisplay: true})}}</td>
                    <td class="billing-header billing-cell">Total Allocated</td>
                    <td :class="allocatedClass">{{prettyCurrency(refundFullfillmentStore.GetTransferAmounts(refundFullfillmentStore.transferAmountType.allocated))}}</td>
                </tr>
                <tr>
                    <td class="billing-header billing-cell">Available Credit</td>
                    <td class="billing-cell text-end">{{prettyCurrency(refundFullfillmentStore.GetTransferAmounts(refundFullfillmentStore.transferAmountType.availableCredit))}}</td>
                    <td class="billing-header billing-cell">Updated Credit</td>
                    <td :class="updatedCreditClass">{{prettyCurrency(refundFullfillmentStore.GetTransferAmounts(refundFullfillmentStore.transferAmountType.updatedCredit))}}</td>
                    <td class="billing-header billing-cell">Available Balance</td>
                    <td class="billing-cell text-end">{{prettyCurrency(refundFullfillmentStore.GetTransferAmounts(refundFullfillmentStore.transferAmountType.availableBalance))}}</td>
                    <td class="billing-header billing-cell">Updated Balance</td>
                    <td :class="updatedBalanceClass">{{prettyCurrency(refundFullfillmentStore.GetTransferAmounts(refundFullfillmentStore.transferAmountType.updatedBalance))}}</td>
                </tr>
                <tr>
                    <td class="billing-header billing-cell">Payment Method</td>
                    <td colspan=3 class="billing-cell">
                        <SimpleList v-if="showTransfers"
                            :listName="ClaimSimpleLists.PaymentMethod" 
                            :includeHeader="false"
                            :includeBlank="false" 
                            v-model:selected="formData.paymentMethodID"
                            @onChange="onChangePaymentMethod" 
                        />
                    </td>
                    <td class="billing-header billing-cell">Payment Type</td>
                    <td colspan=3 class="billing-cell">
                        <SimpleList v-if="showTransfers"
                            :listName="ClaimSimpleLists.RefundPaymentType"
                            :listWarning="transferTypeWarning"
                            :includeHeader="true"
                            :includeBlank="false" 
                            v-model:selected="formData.paymentTypeID"
                            @onChange="onChangePaymentType" 
                        />
                    </td>
                </tr>
            </table>
        </div> 
    </div>  
    <div class="row skinnyscroll pt-3" :style="{'overflow-y': 'scroll', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight + 'rem')}">
        <div class="col">
            <ClaimList v-if="showTransfers"
                :saveEnabled="props.saveEnabled"
                :selection="false"
                :autodistribute="props.autodistribute"
                :claimList = "formData.claimRefundListing"
                :display="ClaimDisplay.RefundTransfer" 
                :displayProcedures="false"
                :displayPayments="false"
                :displayComments="false"
            />
        </div>
        <div class="col pt-1">
            <ClaimList v-if="showTransfers"
                :saveEnabled="props.saveEnabled"
                :selection="false"
                :autodistribute="props.autodistribute"
                :claimList = "formData.claimTransferListing"
                :display="ClaimDisplay.Transfer" 
                :displayProcedures="true"
                :displayPayments="false"
                :displayComments="false"
            />
        </div>
    </div>
    <div :style="{'overflow': 'hidden', 'height': (formData.window.footerHeight + 'rem')}">  
        <div class="row pt-2">
            <div class="col">
                <TextInput 
                    :rows=2
                    :cssFramework="'vanilla'"  
                    v-model:textValue="formData.notes" 
                    :placeholder="'Transfer Notes'" 
                />
            </div>
        </div>
        <div class="row pt-2"> 
            <div class="col-10">
                <span v-show="refundFullfillmentStore.transferTotals.AllocateError" class="fst-italic label-isalus-small text-danger ps-2">Amount allocated ({{ prettyCurrency(refundFullfillmentStore.transferTotals.Allocated) }}) exceeds credited amounts ({{ prettyCurrency(refundFullfillmentStore.transferTotals.UpdatedCredit) }}).</span>
                <span v-show="refundFullfillmentStore.transferTotals.TransferError" class="fst-italic label-isalus-small text-danger ps-2">Amount entered exceeds procedure balance.</span>
                <span v-show="refundFullfillmentStore.refund_error" class="fst-italic label-isalus-small text-danger">Amount entered exceeds credited amount.</span>
                <span v-show="refundFullfillmentStore.transferTotals.AllocateErrorLessThan" class="fst-italic label-isalus-small text-danger ps-2">Amount allocated ({{ prettyCurrency(refundFullfillmentStore.transferTotals.Allocated) }}) is less than credited amounts ({{ prettyCurrency(refundFullfillmentStore.transferTotals.UpdatedCredit) }}), they must equal. Update your source action amounts.</span>
            </div>
            <div class="col-2 text-end">
                <ActionButtonV2 
                    :disabled="refundFullfillmentStore.transfer_save_disabled"
                    :action="ActionButtonStyle.Save"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Transferring'"
                    :performAction="saveTransfers"
                />
            </div>
        </div>
    </div>
</template>
